import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Subject, Subscription } from 'rxjs';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { BaseServices } from '../../kuba.services';
import { saveBlob } from '../safety-rounds/safety-round.service';
import { FlexiSafetyRoundLookupService } from '../safety-rounds/safety-round-lookup.service';
import { filterAssessments, percentOf, reportFileName } from './exposure-register.logic';
import {
  AssignedExposureTemplate,
  ExposureAssessmentStatus,
  ExposureAssessmentStep,
  ExposureAssessmentSummary,
  ExposureProfile,
} from './exposure-register.models';
import { FlexiExposureRegisterService } from './exposure-register.service';

/** The editable basis texts, as typed. */
interface ProfileDraft {
  industryExposures: string;
  productsAndServices: string;
  localClarifications: string;
}

/**
 * Flexi › Exposure Register: the business's exposure assessments and the
 * "About the company" basis texts they are built on.
 *
 * The assessment in progress and the last completed one get a card each;
 * every completed one is in the table. Starting, deleting and editing the
 * basis texts follow the backend's permission probe; everyone else reads.
 */
@Component({
  selector: 'flexi-exposure-register-overview',
  templateUrl: './exposure-register-overview.component.html',
  styleUrls: ['./exposure-register-overview.component.scss'],
  standalone: false,
})
export class ExposureRegisterOverviewComponent implements OnInit, OnDestroy {
  readonly Status = ExposureAssessmentStatus;
  readonly percentOf = percentOf;
  readonly totalSteps = 4;

  canAuthor = false;
  loading = true;
  loadFailed = false;
  assessments: ExposureAssessmentSummary[] = [];
  /** Completed assessments, newest first. */
  completed: ExposureAssessmentSummary[] = [];
  visibleCompleted: ExposureAssessmentSummary[] = [];
  search = '';
  firstCompleted = 0;
  inProgress: ExposureAssessmentSummary | null = null;
  lastCompleted: ExposureAssessmentSummary | null = null;

  /** Null until the department lookup answers; a failed lookup stays null. */
  departmentCount: number | null = null;

  /* ---- About the company ---- */
  profile: ExposureProfile | null = null;
  profileLoading = false;
  profileFailed = false;
  profileDraft: ProfileDraft = { industryExposures: '', productsAndServices: '', localClarifications: '' };
  savingProfile = false;

  /* ---- Start ---- */
  startOpen = false;
  templates: AssignedExposureTemplate[] = [];
  templatesLoading = false;
  selectedTemplateId: number | null = null;
  starting = false;

  /** Assessments with a request in flight (delete, report), with its status key. */
  busyActions = new Map<number, string>();

  private loadedBusinessId: number | null = null;
  private businessRequests = new Subscription();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: FlexiExposureRegisterService,
    private lookups: FlexiSafetyRoundLookupService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private confirmation: ConfirmationService,
    private notifications: NotificationService,
  ) {}

  get businessId(): number {
    const businessRoute = this.route.pathFromRoot.find(route => route.routeConfig?.path === 'business/:id');
    return Number(businessRoute?.snapshot.paramMap.get('id')) || BaseServices.BusinessId;
  }

  get profileDirty(): boolean {
    const saved = this.toDraft(this.profile);
    return saved.industryExposures !== this.profileDraft.industryExposures
      || saved.productsAndServices !== this.profileDraft.productsAndServices
      || saved.localClarifications !== this.profileDraft.localClarifications;
  }

  get hasProfileText(): boolean {
    return !!(this.profile?.IndustryExposures || this.profile?.ProductsAndServices || this.profile?.LocalClarifications);
  }

  ngOnInit(): void {
    this.loadBusiness();
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.loadedBusinessId !== this.businessId) {
          this.loadBusiness();
        }
      });
  }

  ngOnDestroy(): void {
    this.businessRequests.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadBusiness(): void {
    const businessId = this.businessId;
    this.loadedBusinessId = businessId;
    this.businessRequests.unsubscribe();
    this.businessRequests = new Subscription();
    this.canAuthor = false;
    this.startOpen = false;
    this.departmentCount = null;
    this.search = '';
    this.businessRequests.add(
      this.api.permissions(businessId).pipe(takeUntil(this.destroy$)).subscribe(permissions => {
        if (businessId === this.businessId) this.canAuthor = permissions.CanAuthor;
      }),
    );
    this.businessRequests.add(
      this.lookups.businessDepartments(businessId).pipe(takeUntil(this.destroy$)).subscribe(rows => {
        if (businessId === this.businessId) this.departmentCount = rows.length;
      }),
    );
    this.load();
    this.loadProfile();
  }

  load(): void {
    const businessId = this.businessId;
    this.loading = true;
    this.loadFailed = false;
    this.assessments = [];
    this.completed = [];
    this.visibleCompleted = [];
    this.inProgress = null;
    this.lastCompleted = null;
    this.businessRequests.add(
      this.api
        .list(businessId)
        .pipe(takeUntil(this.destroy$), finalize(() => (this.loading = false)))
        .subscribe({
          next: rows => {
            if (businessId !== this.businessId) return;
            this.assessments = rows;
            this.completed = rows
              .filter(row => row.Status === ExposureAssessmentStatus.Completed)
              .sort((a, b) => (b.CompletedOn ?? '').localeCompare(a.CompletedOn ?? '') || b.Id - a.Id);
            this.inProgress = rows.find(row => row.Status === ExposureAssessmentStatus.InProgress) ?? null;
            this.lastCompleted = this.completed[0] ?? null;
            this.applySearch();
            this.scrollToRequestedCard();
          },
          error: () => {
            if (businessId === this.businessId) this.loadFailed = true;
          },
        }),
    );
  }

  applySearch(): void {
    this.firstCompleted = 0;
    this.visibleCompleted = filterAssessments(this.completed, this.search);
  }

  open(assessment: ExposureAssessmentSummary): void {
    this.router.navigate([assessment.Id], { relativeTo: this.route });
  }

  stepKey(step: ExposureAssessmentStep): string {
    return `FLEXI_EXPOSURE.STEP.${step}`;
  }

  /** "Assess the 7 remaining factors, then review the summary." */
  nextStepText(assessment: ExposureAssessmentSummary): string {
    if (assessment.IncludedDepartments === 0) {
      return this.translate.instant('FLEXI_EXPOSURE.OVERVIEW.NEXT_DEPARTMENTS');
    }
    const remaining = assessment.TotalFactors - assessment.AssessedFactors;
    return remaining > 0
      ? this.translate.instant('FLEXI_EXPOSURE.OVERVIEW.NEXT_FACTORS', { count: remaining })
      : this.translate.instant('FLEXI_EXPOSURE.OVERVIEW.NEXT_COMPLETE');
  }

  /* ---------------- Report and delete ---------------- */

  downloadReport(assessment: ExposureAssessmentSummary): void {
    if (this.busyActions.has(assessment.Id)) {
      return;
    }
    this.busyActions.set(assessment.Id, 'FLEXI_EXPOSURE.FEEDBACK.REPORT');
    this.api
      .downloadReport(this.businessId, assessment.Id)
      .pipe(takeUntil(this.destroy$), finalize(() => this.busyActions.delete(assessment.Id)))
      .subscribe({
        next: file => saveBlob(file.blob, file.fileName ?? reportFileName(assessment.Title)),
        // The shared HTTP interceptor reports the failure; finalize enables a retry.
        error: () => undefined,
      });
  }

  confirmDelete(assessment: ExposureAssessmentSummary): void {
    if (!this.canAuthor || this.busyActions.has(assessment.Id)) {
      return;
    }
    this.confirmation.confirm({
      key: 'ferList',
      header: this.translate.instant('CONFIRMATION'),
      message: this.translate.instant(assessment.Status === ExposureAssessmentStatus.Completed
        ? 'FLEXI_EXPOSURE.OVERVIEW.CONFIRM_DELETE_COMPLETED'
        : 'FLEXI_EXPOSURE.OVERVIEW.CONFIRM_DELETE', { title: assessment.Title }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('SWITCH_YES'),
      rejectLabel: this.translate.instant('SWITCH_NO'),
      accept: () => {
        if (this.busyActions.has(assessment.Id)) return;
        this.busyActions.set(assessment.Id, 'FLEXI_EXPOSURE.FEEDBACK.DELETING');
        this.api
          .delete(this.businessId, assessment.Id)
          .pipe(takeUntil(this.destroy$), finalize(() => this.busyActions.delete(assessment.Id)))
          .subscribe({
            next: () => {
              this.notifications.success('UI_NOTIFICATIONS.DELETED');
              this.load();
            },
            // The interceptor reports the failure; refresh in case the list changed elsewhere.
            error: () => this.load(),
          });
      },
    });
  }

  /* ---------------- Start ---------------- */

  /**
   * Opens the start flow. With one assigned template the assessment starts at
   * once; with several, a dialog asks which. An assessment already in progress
   * is offered instead — the backend allows only one at a time.
   */
  openStart(): void {
    if (!this.canAuthor || this.starting || this.templatesLoading) {
      return;
    }
    if (this.inProgress) {
      this.templates = [];
      this.startOpen = true;
      return;
    }
    this.templatesLoading = true;
    this.api
      .templates(this.businessId)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.templatesLoading = false)))
      .subscribe({
        next: templates => {
          this.templates = templates;
          if (!templates.length) {
            this.notifications.warn('FLEXI_EXPOSURE.START.NO_TEMPLATES');
            return;
          }
          if (templates.length === 1) {
            this.start(templates[0].Id);
            return;
          }
          this.selectedTemplateId = (templates.find(t => t.IsDefault) ?? templates[0]).Id;
          this.startOpen = true;
        },
        // The interceptor reports the failure; the button stays available for a retry.
        error: () => undefined,
      });
  }

  closeStart(): void {
    if (!this.starting) {
      this.startOpen = false;
    }
  }

  continueInProgress(): void {
    const assessment = this.inProgress;
    this.startOpen = false;
    if (assessment) {
      this.open(assessment);
    }
  }

  start(templateId: number | null = this.selectedTemplateId): void {
    if (this.starting || templateId === null) {
      return;
    }
    this.starting = true;
    this.api
      .start(this.businessId, { TemplateId: templateId, Title: null })
      .pipe(takeUntil(this.destroy$), finalize(() => (this.starting = false)))
      .subscribe({
        next: id => {
          this.startOpen = false;
          this.router.navigate([id], {
            relativeTo: this.route,
            queryParams: { step: ExposureAssessmentStep.Introduction },
          });
        },
        // A 409 means another assessment was started meanwhile; show it.
        error: () => this.load(),
      });
  }

  templateMeta(template: AssignedExposureTemplate): string {
    return this.translate.instant('FLEXI_EXPOSURE.START.TEMPLATE_CONTENT', {
      factors: template.FactorCount,
      sections: template.SectionCount,
      version: template.Version,
    });
  }

  /* ---------------- About the company ---------------- */

  loadProfile(): void {
    const businessId = this.businessId;
    this.profileLoading = true;
    this.profileFailed = false;
    this.businessRequests.add(
      this.api
        .profile(businessId)
        .pipe(takeUntil(this.destroy$), finalize(() => (this.profileLoading = false)))
        .subscribe({
          next: profile => {
            if (businessId !== this.businessId) return;
            this.profile = profile;
            this.profileDraft = this.toDraft(profile);
          },
          error: () => {
            if (businessId !== this.businessId) return;
            this.profile = null;
            this.profileFailed = true;
          },
        }),
    );
  }

  cancelProfile(): void {
    this.profileDraft = this.toDraft(this.profile);
  }

  saveProfile(): void {
    if (!this.canAuthor || this.savingProfile || !this.profileDirty) {
      return;
    }
    const request = {
      IndustryExposures: this.profileDraft.industryExposures.trim() || null,
      ProductsAndServices: this.profileDraft.productsAndServices.trim() || null,
      LocalClarifications: this.profileDraft.localClarifications.trim() || null,
    };
    this.savingProfile = true;
    this.api
      .saveProfile(this.businessId, request)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.savingProfile = false)))
      .subscribe({
        next: () => {
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
          // Re-read for "Last saved … by …"; the edits stay if that fails.
          this.loadProfile();
        },
        // The interceptor reports the failure; the edits stay for a retry.
        error: () => undefined,
      });
  }

  private toDraft(profile: ExposureProfile | null): ProfileDraft {
    return {
      industryExposures: profile?.IndustryExposures ?? '',
      productsAndServices: profile?.ProductsAndServices ?? '',
      localClarifications: profile?.LocalClarifications ?? '',
    };
  }

  /** Step 1's "Edit" link lands here with #fer-about; bring the card into view. */
  private scrollToRequestedCard(): void {
    if (this.route.snapshot.fragment !== 'fer-about') {
      return;
    }
    setTimeout(() => document.getElementById('fer-about')?.scrollIntoView({ block: 'start' }));
  }
}
